import { Component, OnInit } from '@angular/core';
import { SettingsService } from 'src/app/services/settings.service';
import { TransactionService } from 'src/app/services/transaction.service';
import { ChartData } from 'src/app/shared/models/general/chart-data';
import { SearchRequest } from 'src/app/shared/models/general/search-request';
import { TransactionDataBundle } from 'src/app/shared/models/transactions/transaction-data-bundle';
import * as moment from 'moment';
import { ChartColors } from 'src/app/shared/models/general/chart-colors';
import { environment } from 'src/environments/environment';
import { UserFiaPins } from 'src/app/shared/models/users/user-fia-pins';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  //General
  busy: boolean = false;
  welcomCardBusy: boolean = false;
  statCardsBusy: boolean = true;
  showError: boolean = false;
  errorMsg = '';
  currentYear = new Date().getFullYear().toString();
  traded: boolean = false;
  totalPinBalance: number = 0;

  extraInfo = environment.production ? '' : ' [' + environment.apiurl + ']';

  //Weekly Stat cards
  statCardsSearchRequest: SearchRequest = new SearchRequest();
  statCardsTransactionDataBundle: TransactionDataBundle[] = [];
  userFiaPins: UserFiaPins[] = [];
  statCardsFiaAvailable: number = 0;
  statCardsSdaAvailable: number = 0;

  //Main yearly summary bar chart
  public chart1Type: string = 'bar';
  public chartDatasets: Array<ChartData> = [];
  public chartLabels: Array<any> = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];  
  public chartColors: ChartColors[] = [];
  public chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            callback: function (value: number) {
              return 'R' + value.toString();
            },
          },
        },
      ],
    },
    tooltips: {
      callbacks: {
        label: function (tooltipItem: any) {
          var data: number = tooltipItem.yLabel;
          return 'R ' + data.toString();
        },
      },
    },
  };
  yearlySummarySearchRequest: SearchRequest = new SearchRequest();
  yearlySummaryTransactionDataBundle: TransactionDataBundle[] = [];
  yearlySummaryBusy: boolean = false;

  constructor(
    public settingsService: SettingsService,
    public transactionsService: TransactionService,
    public userService: UserService
  ) {}

  async ngOnInit() {
    this.busy = true;
    this.settingsService.ValidateGlobalVars().then(() => {
      this.GetTransactionData();
      //this.GetTransactionDataForYearByMonth();
      this.busy = false;
    });
  }

  GetTransactionData() {
    this.statCardsBusy = true;
    this.yearlySummaryBusy = true;
    this.errorMsg = '';
    this.showError = false;

    this.statCardsSearchRequest.id = this.settingsService.user.id;
    this.statCardsSearchRequest.entityType = this.settingsService.userEntity.id;
    this.statCardsSearchRequest.timePeriod = 2; //Month
    this.statCardsSearchRequest.fromDate = moment()
      .startOf('year')
      .format('YYYY-MM-DD');
    this.statCardsSearchRequest.toDate = moment().format('YYYY-MM-DD');

    this.transactionsService
      .GetTransactionDataBundleForEntity(this.statCardsSearchRequest)
      .toPromise()
      .then(
        (response) => {
          this.statCardsTransactionDataBundle = response;
          this.statCardsFiaAvailable =
            this.statCardsTransactionDataBundle[0].tradingStatistics.fiaAvailable;
          this.statCardsSdaAvailable =
            this.statCardsTransactionDataBundle[0].tradingStatistics.sdaAvailable;

          this.SetupYearlySummaryGraph(
            this.statCardsTransactionDataBundle
          ).then(() => {
            this.yearlySummaryBusy = false;
          });
        },
        (error) => {
          this.showError = true;
          this.errorMsg = error.error.ErrorDetail;
          this.statCardsBusy = false;
          this.yearlySummaryBusy = false;
        }
      );

    this.userService
      .GetUsersFiaPins(this.settingsService.user.id)
      .toPromise()
      .then(
        (response) => {
          this.userFiaPins = response;
          this.totalPinBalance = UserFiaPins.calculatePinAvailableAmount(
            this.userFiaPins
          );
          this.showError = false;
          this.busy = false;
        },
        (error) => {
          this.showError = true;
          this.errorMsg =
            'Oops: ' +
            error.error.title +
            ' while trying to get your FIA pins data';
          this.busy = false;
        }
      );
  }

  GetTransactionDataForYearByMonth() {
    this.yearlySummaryBusy = true;
    this.errorMsg = '';
    this.showError = false;

    this.yearlySummarySearchRequest.id = this.settingsService.user.id;
    this.yearlySummarySearchRequest.entityType =
      this.settingsService.userEntity.id;
    this.yearlySummarySearchRequest.timePeriod = 2; //month
    this.yearlySummarySearchRequest.fromDate = new Date(
      new Date().getFullYear(),
      0,
      1
    ).toString();
    this.yearlySummarySearchRequest.toDate = moment().format('YYYY-MM-DD');

    this.transactionsService
      .GetTransactionDataBundleForEntity(this.yearlySummarySearchRequest)
      .toPromise()
      .then(
        (response) => {
          this.yearlySummaryTransactionDataBundle = response;
          this.SetupYearlySummaryGraph(
            this.yearlySummaryTransactionDataBundle
          ).then(() => {
            this.yearlySummaryBusy = false;
          });
        },
        (error) => {
          this.showError = true;
          this.errorMsg = error.error.ErrorDetail;
          this.yearlySummaryBusy = false;
        }
      );
  }

  SetupYearlySummaryGraph(data: TransactionDataBundle[]) {
    return new Promise((resolve) => {
      //this.chartLabels = [];
      this.chartDatasets = [];
      var chartdata: ChartData = new ChartData();
      var chartColor: ChartColors = new ChartColors();
      
      this.traded = data.find((x) => x.noOfTrades > 0) != undefined ? true : false;
      data.forEach((x) => {        
        chartdata.data.push(x.totalTradeProfit);
        chartColor.backgroundColor.push('#286B70');
        //chartColor.borderColor.push('rgba(0, 200, 81, 1)');
      });

      chartdata.label = 'Total Monthly Profit';
      this.chartDatasets.push(chartdata);
      this.chartColors.push(chartColor);
      resolve(true);
    });
  }
}
